import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { IconCheck, IconCheckDouble, IconSend, IconClock, IconWarning } from '../icons';
import { StatusChip } from './common/StatusChip';
import type { BadgeTone } from './common/Badge';
import { formatDate, DateInput } from '../utils/formatters';

/**
 * `EmailStatusChip` (M-D, architecture-ux-density-v2.md §5.7 + §9).
 *
 * Thin domain wrapper around {@link StatusChip} for the invoice/offer e-mail
 * delivery lifecycle. Shared 1:1 by `InvoiceArchive` and `OfferArchive` — one
 * component, one visual language.
 *
 * Kurzform (visible token, Daniel-Entscheidung D2 „Nur Symbol"): the visible
 * chip shows ONLY the symbol icon — no word next to it. The status word
 * (`common:emailSend.statusShort.*`) is exposed as a screenreader-only
 * `srLabel` (StatusChip renders it as `<span class="sr-only">`) and is
 * additionally shown as a visible line inside the popover. Symbol language
 * (§9, Daniel-Entscheidung): `queued` → clock, `sent` → paper plane,
 * `delivered` → single check, `opened` → DOUBLE check (messenger "read"
 * semantics), `bounced`/`failed` → warning triangle.
 *
 * Langform (popover, `common:emailSend.detail.*`): short status word +
 * recipient + timestamp chain (sent/delivered/opened via
 * `formatDate(x, { locale, includeTime: true })`) and `bounceReason` when
 * bounced. The „(laut E-Mail-Dienst)"-Nuance for `delivered` lives exclusively
 * in `detail.delivered` (popover), not in the (now icon-only) visible chip.
 *
 * `emailStatus === undefined` ⇒ renders `null` (parity with the replaced
 * inline mini-text, which was only rendered when `emailStatus` existed).
 */
export type EmailDeliveryStatus =
  | 'sent'
  | 'delivered'
  | 'opened'
  | 'bounced'
  | 'failed'
  | 'queued';

export interface EmailStatusChipProps {
  emailStatus:
    | {
        status: EmailDeliveryStatus;
        sentAt?: unknown;
        deliveredAt?: unknown;
        openedAt?: unknown;
        recipientEmail?: string;
        bounceReason?: string;
      }
    | undefined;
  /** i18n.language, für die Zeitstempel-Formatierung im Popover. */
  locale: string;
  'data-testid'?: string;
}

/** Tone-Zuordnung (§5.7): opened→success, delivered/sent→info, queued→neutral, bounced/failed→danger. */
const TONE_BY_STATUS: Record<EmailDeliveryStatus, BadgeTone> = {
  queued: 'neutral',
  sent: 'info',
  delivered: 'info',
  opened: 'success',
  bounced: 'danger',
  failed: 'danger',
};

/** Symbolsprache (§9). Icons sind in StatusChip `aria-hidden` gewrappt. */
const ICON_BY_STATUS: Record<EmailDeliveryStatus, ReactNode> = {
  queued: (
    <span data-testid="email-icon-queued">
      <IconClock size={14} />
    </span>
  ),
  sent: (
    <span data-testid="email-icon-sent">
      <IconSend size={14} />
    </span>
  ),
  delivered: (
    <span data-testid="email-icon-delivered">
      <IconCheck size={14} />
    </span>
  ),
  opened: (
    <span data-testid="email-icon-opened">
      <IconCheckDouble size={14} />
    </span>
  ),
  bounced: (
    <span data-testid="email-icon-bounced">
      <IconWarning size={14} />
    </span>
  ),
  failed: (
    <span data-testid="email-icon-failed">
      <IconWarning size={14} />
    </span>
  ),
};

/** Anzeige-Konvention für „kein Wert" (deckungsgleich mit formatDate). */
const EMPTY = '—';

export function EmailStatusChip({
  emailStatus,
  locale,
  'data-testid': dataTestId,
}: EmailStatusChipProps) {
  const { t } = useTranslation(['common']);

  // undefined ⇒ render null (Bestandsverhalten des ersetzten Mini-Texts).
  if (!emailStatus) return null;

  const { status, sentAt, deliveredAt, openedAt, recipientEmail, bounceReason } = emailStatus;

  // Fail-safe gegen unerwartete Firestore-Werte: unbekannter Status ⇒ neutraler
  // Chip ohne Symbol, statt eine `undefined`-Tone-Klasse zu erzeugen.
  const tone = TONE_BY_STATUS[status] ?? 'neutral';
  const icon = ICON_BY_STATUS[status];

  const fmt = (value: unknown): string =>
    formatDate(value as DateInput, { locale, includeTime: true });

  const recipient = recipientEmail || EMPTY;

  // Kurzwort (§10.2 `emailSend.statusShort.*`): NUR für Screenreader sichtbar
  // (StatusChip `srLabel`) + zusätzlich als sichtbare Zeile im Popover.
  const shortLabel = t(`common:emailSend.statusShort.${status}`);

  // Fail-safe-Startwert (deckt unerwartete Firestore-Status ab, die der
  // erschöpfende switch typseitig als `never` behandelt): Kurzform-Text.
  let sentence = t(`common:emailSend.status.${status}`);
  switch (status) {
    case 'queued':
      sentence = t('common:emailSend.detail.queued', { recipient });
      break;
    case 'sent':
      sentence = t('common:emailSend.detail.sent', { recipient, date: fmt(sentAt) });
      break;
    case 'delivered':
      sentence = t('common:emailSend.detail.delivered', { recipient, date: fmt(deliveredAt) });
      break;
    case 'opened':
      sentence = t('common:emailSend.detail.opened', {
        date: fmt(openedAt),
        deliveredDate: fmt(deliveredAt),
      });
      break;
    case 'bounced':
      sentence = t('common:emailSend.detail.bounced', { reason: bounceReason || EMPTY });
      break;
    case 'failed':
      sentence = t('common:emailSend.detail.failed');
      break;
  }

  // Empfänger explizit ausweisen, wo der Satz ihn nicht ohnehin enthält
  // (opened/bounced/failed). §5.7 verlangt „Empfänger" in der Langform.
  const showRecipientLine =
    Boolean(recipientEmail) && (status === 'opened' || status === 'bounced' || status === 'failed');

  // Kurzwort steht zusätzlich (sichtbar) im Popover, vor der Detail-Langform.
  const detail = (
    <div className="flex flex-col gap-0.5">
      <span className="font-semibold text-primary">{shortLabel}</span>
      <span>{sentence}</span>
      {showRecipientLine ? (
        <span className="text-muted">
          {t('common:emailSend.detail.recipientLabel')}: {recipientEmail}
        </span>
      ) : null}
    </div>
  );

  return (
    <StatusChip
      tone={tone}
      icon={icon}
      // Daniel-Entscheidung D2 „Nur Symbol": sichtbares Label bleibt leer —
      // NUR das Icon ist sichtbar. Das Kurzwort geht als `srLabel` an
      // StatusChip (rendert `<span class="sr-only">`) und steht zusätzlich
      // sichtbar im Popover (s. `detail` oben).
      label={null}
      srLabel={shortLabel}
      detailTitle={t('common:emailSend.detail.title')}
      detail={detail}
      data-testid={dataTestId ?? `email-status-chip-${status}`}
    />
  );
}

export default EmailStatusChip;
